/**
 * WebhookSettingsService
 *
 * Service for reading webhook configuration from Firestore settings/webhooks document.
 * This is a singleton service as webhook settings are global (not scoped to project).
 *
 * Features:
 * - Read webhook URLs from Firestore: settings/webhooks
 * - Validate required webhook URLs are configured
 * - Type-safe: returns WebhookSettings type from schema
 *
 * Usage:
 * ```typescript
 * import { WebhookSettingsService } from '@/modules/firebase-core/services/WebhookSettingsService';
 *
 * const service = new WebhookSettingsService();
 *
 * // Get webhook URLs
 * const settings = await service.getWebhookUrls();
 * if (settings?.ai_strategy) {
 *   // Use URL for AI strategy creation
 *   await fetch(settings.ai_strategy, { ... });
 * }
 *
 * // Validate all required URLs are configured
 * try {
 *   await service.validateWebhookUrls();
 * } catch (error) {
 *   // Show error to user: missing webhook configuration
 * }
 * ```
 */

import {
  doc,
  getDoc,
  FirestoreDataConverter
} from 'firebase/firestore';
import type { WebhookSettings } from '@segmently/schema-models/settings';
import { CoreFirebaseService } from './CoreFirebaseService';
import { webhookSettingsConverter as clientWebhookSettingsConverter } from '../converters/webhookSettingsConverter';

/**
 * WebhookSettingsService
 *
 * Provides access to webhook configuration stored in Firestore.
 * Webhooks are stored in a single document: settings/webhooks
 *
 * This service is lightweight and can be instantiated per-component if needed,
 * as it only reads from a single global document.
 */
export class WebhookSettingsService {
  private readonly collectionName = 'settings';
  private readonly documentId = 'webhooks';
  private readonly firestore = CoreFirebaseService.getInstance().firestore;
  private readonly converter: FirestoreDataConverter<WebhookSettings>;

  /**
   * Create a new WebhookSettingsService instance
   *
   * @param converter - Firestore converter for WebhookSettings (defaults to client SDK converter)
   */
  constructor(converter: FirestoreDataConverter<WebhookSettings> = clientWebhookSettingsConverter) {
    this.converter = converter;
  }

  /**
   * Get webhook URLs from Firestore
   *
   * Reads the settings/webhooks document and returns all configured webhook URLs.
   *
   * @returns {Promise<WebhookSettings | null>} The webhook settings or null if not configured
   *
   * @example
   * ```typescript
   * const settings = await service.getWebhookUrls();
   * if (settings?.ai_strategy) {
   *   console.log('AI Strategy webhook:', settings.ai_strategy);
   * } else {
   *   console.error('AI Strategy webhook not configured');
   * }
   * ```
   */
  async getWebhookUrls(): Promise<WebhookSettings | null> {
    try {
      const docRef = doc(this.firestore, this.collectionName, this.documentId).withConverter(this.converter);
      const docSnap = await getDoc(docRef);

      if (!docSnap.exists()) {
        console.warn('[WebhookSettingsService] Webhook settings document not found');
        return null;
      }

      const data = docSnap.data();
      return { ...data, id: docSnap.id } as WebhookSettings;
    } catch (error) {
      CoreFirebaseService.getInstance().logError(error, 'WebhookSettingsService.getWebhookUrls');
      throw error;
    }
  }

  /**
   * Validate that all required webhook URLs are configured
   *
   * Checks that the following webhook URLs are present:
   * - descriptions
   * - audiences
   * - actors
   * - questions
   * - interviews
   * - ai_actor
   * - strategyblocks
   * - ai_strategy
   * - structuredoutput
   *
   * @throws {Error} If any required webhook URL is missing
   *
   * @example
   * ```typescript
   * try {
   *   await service.validateWebhookUrls();
   *   console.log('All webhook URLs configured');
   * } catch (error) {
   *   console.error('Missing webhook URLs:', error.message);
   *   // Show error to user
   * }
   * ```
   */
  async validateWebhookUrls(): Promise<void> {
    try {
      const settings = await this.getWebhookUrls();

      if (!settings) {
        throw new Error('Webhook settings not configured. Please configure webhook URLs in the Settings page.');
      }

      // Check required webhook URLs (from old code: apiService.js line 242-244)
      const requiredUrls: (keyof WebhookSettings)[] = [
        'descriptions',
        'audiences',
        'actors',
        'questions',
        'interviews',
        'aiActor',
        'strategyblocks',
        'aiStrategy',
        'structuredoutput'
      ];

      const missingUrls = requiredUrls.filter(urlKey => !settings[urlKey]);

      if (missingUrls.length > 0) {
        throw new Error(
          `Missing webhook URLs in settings: ${missingUrls.join(', ')}. ` +
          'Please configure all webhook URLs in the Settings page.'
        );
      }
    } catch (error) {
      if (error instanceof Error) {
        // Re-throw validation errors as-is
        throw error;
      }
      // Wrap unknown errors
      CoreFirebaseService.getInstance().logError(error, 'WebhookSettingsService.validateWebhookUrls');
      throw new Error('Failed to validate webhook URLs');
    }
  }
}
